/**
 * Email Templates Editor Page
 * 
 * Manage customizable email templates for configurator notifications.
 * Supports: new_offer, order_confirmation, status_change templates.
 * 
 * MAGE-206
 */

import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import configuratorService from '../services/configuratorService';

// ==========================================
// Styled Components
// ==========================================

const PageContainer = styled.div`
  min-height: 100vh;
  background: #f3f4f6;
  padding: 1.5rem;
`;

const Header = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
`;

const HeaderTitle = styled.div`
  h1 {
    font-size: 1.5rem;
    font-weight: 700;
    color: #1f2937;
    margin: 0 0 0.25rem 0;
  }
  p {
    color: #6b7280;
    font-size: 0.875rem;
    margin: 0;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const Button = styled.button`
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  
  &.primary {
    background: #3b82f6;
    color: white;
    border: none;
    
    &:hover { background: #2563eb; }
  }
  
  &.secondary {
    background: white;
    color: #374151;
    border: 1px solid #d1d5db;
    
    &:hover { background: #f9fafb; }
  }
  
  &.danger {
    background: #ef4444;
    color: white;
    border: none;
    
    &:hover { background: #dc2626; }
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: 1.5rem;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const TemplateList = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const TemplateListHeader = styled.div`
  padding: 1rem;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
  font-weight: 600;
  color: #374151;
`;

const TemplateCategory = styled.div`
  border-bottom: 1px solid #e5e7eb;
  
  &:last-child {
    border-bottom: none;
  }
`;

const CategoryHeader = styled.div`
  padding: 0.75rem 1rem;
  background: #f9fafb;
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const TemplateItem = styled.div`
  padding: 0.75rem 1rem;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.2s;
  background: ${props => props.active ? '#eff6ff' : 'white'};
  border-left: 3px solid ${props => props.active ? '#3b82f6' : 'transparent'};
  
  &:hover {
    background: ${props => props.active ? '#eff6ff' : '#f9fafb'};
  }
  
  .name {
    font-size: 0.875rem;
    color: #1f2937;
  }
  
  .badges {
    display: flex;
    gap: 0.25rem;
  }
`;

const Badge = styled.span`
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.625rem;
  font-weight: 600;
  background: ${props => props.bg || '#e5e7eb'};
  color: ${props => props.color || '#374151'};
`;

const EditorPanel = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const EditorHeader = styled.div`
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const EditorTitle = styled.h2`
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
`;

const Tabs = styled.div`
  display: flex;
  border-bottom: 1px solid #e5e7eb;
`;

const Tab = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  background: none;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  color: ${props => props.active ? '#3b82f6' : '#6b7280'};
  border-bottom: 2px solid ${props => props.active ? '#3b82f6' : 'transparent'};
  transition: all 0.2s;
  
  &:hover {
    color: #3b82f6;
  }
`;

const EditorBody = styled.div`
  padding: 1.5rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
  
  label {
    display: block;
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
    margin-bottom: 0.5rem;
  }
  
  input, select, textarea {
    width: 100%;
    padding: 0.5rem 0.75rem;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 0.875rem;
    
    &:focus {
      outline: none;
      border-color: #3b82f6;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }
  }
  
  textarea {
    min-height: 150px;
    font-family: monospace;
    font-size: 0.8125rem;
    line-height: 1.5;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const ColorPicker = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  input[type="color"] {
    width: 40px;
    height: 32px;
    padding: 0;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    cursor: pointer;
  }
  
  input[type="text"] {
    flex: 1;
  }
`;

const VariablesPanel = styled.div`
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
`;

const VariablesList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const VariableTag = styled.button`
  padding: 0.25rem 0.5rem;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 0.75rem;
  font-family: monospace;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #3b82f6;
    color: white;
    border-color: #3b82f6;
  }
`;

const PreviewContainer = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  background: white;
`;

const PreviewHeader = styled.div`
  padding: 0.75rem 1rem;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  
  .subject {
    font-weight: 600;
    color: #1f2937;
  }
`;

const PreviewBody = styled.div`
  min-height: 400px;
  
  iframe {
    width: 100%;
    height: 500px;
    border: none;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
  color: #6b7280;
  
  h3 {
    margin: 1rem 0 0.5rem;
    color: #374151;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  gap: 1.5rem;
  
  label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.875rem;
    color: #374151;
    cursor: pointer;
    
    input[type="checkbox"] {
      width: auto;
    }
  }
`;

const EditorFooter = styled.div`
  padding: 1rem 1.5rem;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

// ==========================================
// Component
// ==========================================

function EmailTemplatesEditor() {
  const [templates, setTemplates] = useState([]);
  const [availableVariables, setAvailableVariables] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [activeTab, setActiveTab] = useState('edit');
  const [previewData, setPreviewData] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    template_type: 'new_offer',
    subject: '',
    body_html: '',
    body_text: '',
    is_active: true,
    is_default: false,
    header_color: '#3b82f6',
    footer_text: '',
    configurator_id: null
  });

  const templateTypeLabels = {
    new_offer: 'New Quote (Admin)',
    order_confirmation: 'Order Confirmation (Customer)',
    status_change: 'Status Change Notification'
  };

  // Load templates
  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const data = await configuratorService.getEmailTemplates();
      setTemplates(data.templates || []);
      setAvailableVariables(data.available_variables || {});
      setError('');
    } catch (err) {
      console.error('Failed to load templates:', err);
      setError('Failed to load email templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  // Group templates by type
  const templatesByType = templates.reduce((acc, t) => {
    if (!acc[t.template_type]) acc[t.template_type] = [];
    acc[t.template_type].push(t);
    return acc;
  }, {});

  // Handle template selection
  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    setFormData({
      name: template.name,
      template_type: template.template_type,
      subject: template.subject,
      body_html: template.body_html,
      body_text: template.body_text || '',
      is_active: template.is_active,
      is_default: template.is_default,
      header_color: template.header_color || '#3b82f6',
      footer_text: template.footer_text || '',
      configurator_id: template.configurator_id
    });
    setActiveTab('edit');
    setPreviewData(null);
  };

  // Handle new template
  const handleNewTemplate = () => {
    setSelectedTemplate(null);
    setFormData({
      name: '',
      template_type: 'new_offer',
      subject: '',
      body_html: getDefaultHtmlTemplate(),
      body_text: '',
      is_active: true,
      is_default: false,
      header_color: '#3b82f6',
      footer_text: '',
      configurator_id: null
    });
    setActiveTab('edit');
  };

  // Get default HTML template
  const getDefaultHtmlTemplate = () => {
    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <style>
    body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
    .container { max-width: 600px; margin: 0 auto; padding: 20px; }
    .header { background: {{header_color}}; color: white; padding: 20px; text-align: center; }
    .content { padding: 20px; background: #f9fafb; }
    .footer { text-align: center; padding: 20px; color: #6b7280; font-size: 0.875rem; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>Email Title</h1>
    </div>
    <div class="content">
      <p>Dear {{customer_name}},</p>
      <p>Your email content here...</p>
    </div>
    <div class="footer">
      Footer text here
    </div>
  </div>
</body>
</html>`;
  };

  // Handle form change
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Insert variable at cursor
  const insertVariable = (variable) => {
    const textarea = document.querySelector('textarea[name="body_html"]');
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newValue = formData.body_html.substring(0, start) + variable + formData.body_html.substring(end);
      setFormData(prev => ({
        ...prev,
        body_html: newValue
      }));
      // Restore cursor position after React update
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + variable.length, start + variable.length);
      }, 0);
    }
  };

  // Handle save
  const handleSave = async () => {
    if (!formData.name || !formData.subject || !formData.body_html) {
      alert('Please fill in all required fields');
      return;
    }
    
    try {
      setSaving(true);
      
      if (selectedTemplate) {
        await configuratorService.updateEmailTemplate(selectedTemplate.id, formData);
      } else {
        const result = await configuratorService.createEmailTemplate(formData);
        setSelectedTemplate(result.template);
      }
      
      await loadTemplates();
      alert('Template saved successfully');
    } catch (err) {
      console.error('Failed to save template:', err);
      alert(err.response?.data?.error || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!selectedTemplate) return;
    if (!window.confirm('Delete this template? This action cannot be undone.')) return;
    
    try {
      await configuratorService.deleteEmailTemplate(selectedTemplate.id);
      setSelectedTemplate(null);
      await loadTemplates();
    } catch (err) {
      console.error('Failed to delete template:', err);
      alert(err.response?.data?.error || 'Failed to delete template');
    }
  };

  // Handle preview
  const handlePreview = async () => {
    if (!selectedTemplate) return;
    
    try {
      const data = await configuratorService.previewEmailTemplate(selectedTemplate.id);
      setPreviewData(data);
      setActiveTab('preview');
    } catch (err) {
      console.error('Failed to preview template:', err);
      alert('Failed to generate preview');
    }
  };

  // Handle seed defaults
  const handleSeedDefaults = async () => {
    if (templates.length > 0) {
      if (!window.confirm('This will add default templates. Continue?')) return;
    }
    
    try {
      await configuratorService.seedEmailTemplates();
      await loadTemplates();
      alert('Default templates created');
    } catch (err) {
      console.error('Failed to seed templates:', err);
      alert(err.response?.data?.error || 'Failed to create default templates');
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          Loading...
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Header>
        <HeaderTitle>
          <h1>✉️ Email Templates</h1>
          <p>Customize email notifications for configurator orders</p>
        </HeaderTitle>
        <HeaderActions>
          <Button className="secondary" onClick={handleSeedDefaults}>
            🔄 Seed Defaults
          </Button>
          <Button className="primary" onClick={handleNewTemplate}>
            ➕ New Template
          </Button>
        </HeaderActions>
      </Header>
      
      {error && (
        <div style={{ padding: '1rem', background: '#fee2e2', color: '#991b1b', borderRadius: '8px', marginBottom: '1rem' }}>
          {error}
        </div>
      )}
      
      <ContentGrid>
        {/* Template List */}
        <TemplateList>
          <TemplateListHeader>Templates ({templates.length})</TemplateListHeader>
          
          {Object.entries(templatesByType).length === 0 ? (
            <EmptyState>
              <span style={{ fontSize: '2rem' }}>📭</span>
              <p>No templates yet</p>
              <Button className="primary" onClick={handleSeedDefaults} style={{ marginTop: '1rem' }}>
                Create Defaults
              </Button>
            </EmptyState>
          ) : (
            Object.entries(templatesByType).map(([type, typeTemplates]) => (
              <TemplateCategory key={type}>
                <CategoryHeader>{templateTypeLabels[type] || type}</CategoryHeader>
                {typeTemplates.map(template => (
                  <TemplateItem
                    key={template.id}
                    active={selectedTemplate?.id === template.id}
                    onClick={() => handleSelectTemplate(template)}
                  >
                    <span className="name">{template.name}</span>
                    <div className="badges">
                      {template.is_default && (
                        <Badge bg="#dbeafe" color="#2563eb">Default</Badge>
                      )}
                      {!template.is_active && (
                        <Badge bg="#fef3c7" color="#d97706">Inactive</Badge>
                      )}
                    </div>
                  </TemplateItem>
                ))}
              </TemplateCategory>
            ))
          )}
        </TemplateList>
        
        {/* Editor Panel */}
        <EditorPanel>
          {!selectedTemplate && !formData.name ? (
            <EmptyState style={{ padding: '6rem 2rem' }}>
              <span style={{ fontSize: '3rem' }}>✉️</span>
              <h3>Select a Template</h3>
              <p>Choose a template from the list or create a new one</p>
            </EmptyState>
          ) : (
            <>
              <EditorHeader>
                <EditorTitle>
                  {selectedTemplate ? 'Edit Template' : 'New Template'}
                </EditorTitle>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {selectedTemplate && (
                    <>
                      <Button className="secondary" onClick={handlePreview}>
                        👁️ Preview
                      </Button>
                      <Button className="danger" onClick={handleDelete}>
                        🗑️ Delete
                      </Button>
                    </>
                  )}
                </div>
              </EditorHeader>
              
              <Tabs>
                <Tab active={activeTab === 'edit'} onClick={() => setActiveTab('edit')}>
                  ✏️ Edit
                </Tab>
                <Tab active={activeTab === 'preview'} onClick={() => selectedTemplate && handlePreview()}>
                  👁️ Preview
                </Tab>
              </Tabs>
              
              <EditorBody>
                {activeTab === 'edit' ? (
                  <>
                    <FormRow>
                      <FormGroup>
                        <label>Template Name *</label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="e.g., Custom Quote Notification"
                        />
                      </FormGroup>
                      
                      <FormGroup>
                        <label>Template Type</label>
                        <select
                          name="template_type"
                          value={formData.template_type}
                          onChange={handleChange}
                          disabled={!!selectedTemplate}
                        >
                          <option value="new_offer">New Quote (Admin)</option>
                          <option value="order_confirmation">Order Confirmation (Customer)</option>
                          <option value="status_change">Status Change Notification</option>
                        </select>
                      </FormGroup>
                    </FormRow>
                    
                    <FormGroup>
                      <label>Email Subject *</label>
                      <input
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        placeholder="e.g., New Quote: {{order_number}}"
                      />
                    </FormGroup>
                    
                    <FormRow>
                      <FormGroup>
                        <label>Header Color</label>
                        <ColorPicker>
                          <input
                            type="color"
                            name="header_color"
                            value={formData.header_color}
                            onChange={handleChange}
                          />
                          <input
                            type="text"
                            name="header_color"
                            value={formData.header_color}
                            onChange={handleChange}
                            pattern="^#[0-9A-Fa-f]{6}$"
                          />
                        </ColorPicker>
                      </FormGroup>
                      
                      <FormGroup>
                        <label>Options</label>
                        <CheckboxGroup>
                          <label>
                            <input
                              type="checkbox"
                              name="is_active"
                              checked={formData.is_active}
                              onChange={handleChange}
                            />
                            Active
                          </label>
                          <label>
                            <input
                              type="checkbox"
                              name="is_default"
                              checked={formData.is_default}
                              onChange={handleChange}
                            />
                            Default
                          </label>
                        </CheckboxGroup>
                      </FormGroup>
                    </FormRow>
                    
                    <VariablesPanel>
                      <label style={{ fontWeight: 500, marginBottom: '0.5rem', display: 'block' }}>
                        Available Variables (click to insert):
                      </label>
                      <VariablesList>
                        {(availableVariables[formData.template_type] || []).map(variable => (
                          <VariableTag
                            key={variable}
                            type="button"
                            onClick={() => insertVariable(variable)}
                          >
                            {variable}
                          </VariableTag>
                        ))}
                      </VariablesList>
                    </VariablesPanel>
                    
                    <FormGroup>
                      <label>Email Body (HTML) *</label>
                      <textarea
                        name="body_html"
                        value={formData.body_html}
                        onChange={handleChange}
                        rows={20}
                        style={{ minHeight: '300px' }}
                      />
                    </FormGroup>
                    
                    <FormGroup>
                      <label>Footer Text</label>
                      <input
                        type="text"
                        name="footer_text"
                        value={formData.footer_text}
                        onChange={handleChange}
                        placeholder="e.g., Questions? Contact us at support@example.com"
                      />
                    </FormGroup>
                  </>
                ) : (
                  <PreviewContainer>
                    <PreviewHeader>
                      <div className="subject">
                        Subject: {previewData?.subject || formData.subject}
                      </div>
                    </PreviewHeader>
                    <PreviewBody>
                      {previewData ? (
                        <iframe
                          srcDoc={previewData.body_html}
                          title="Email Preview"
                          sandbox="allow-same-origin"
                        />
                      ) : (
                        <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
                          Click "Preview" to generate email preview
                        </div>
                      )}
                    </PreviewBody>
                  </PreviewContainer>
                )}
              </EditorBody>
              
              {activeTab === 'edit' && (
                <EditorFooter>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                    {selectedTemplate && (
                      <>Last updated: {new Date(selectedTemplate.updated_at).toLocaleString()}</>
                    )}
                  </div>
                  <Button className="primary" onClick={handleSave} disabled={saving}>
                    {saving ? 'Saving...' : '💾 Save Template'}
                  </Button>
                </EditorFooter>
              )}
            </>
          )}
        </EditorPanel>
      </ContentGrid>
    </PageContainer>
  );
}

export default EmailTemplatesEditor;
